import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, MapPin, ChevronDown } from "lucide-react";
import { PHOTOS, PHONE_TEL, PHONE_DISPLAY, WA_DEFAULT } from "@/data/content";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden bg-forest-dark">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={PHOTOS.building}
          alt="Hotel Anil Palace facade on NH66, Hemmadi"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-forest-dark via-forest-dark/60 to-forest-dark/30" />
      <div className="absolute inset-0 bg-emerald-950/40" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-36 sm:px-8 lg:pb-24"
      >
        <MaskedLine delay={0.35}>
          <span className="inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.35em] text-gold-light">
            <span className="h-px w-10 bg-gold/70" />
            NH66 · Hemmadi · Coastal Karnataka
          </span>
        </MaskedLine>

        <MaskedLine delay={0.5} className="mt-6">
          <span className="font-kannada text-lg font-medium tracking-wide text-gold sm:text-xl">
            ಅನಿಲ್ ಪ್ಯಾಲೇಸ್
          </span>
        </MaskedLine>

        <h1 className="mt-2 font-display font-semibold uppercase leading-[0.95] tracking-tight text-amber-50">
          <MaskedLine delay={0.62} className="text-5xl sm:text-6xl lg:text-6xl">
            Anil Palace
          </MaskedLine>
          <MaskedLine delay={0.74} className="text-3xl italic normal-case text-gold-gradient sm:text-4xl lg:text-5xl">
            a royal halt on the coastal highway
          </MaskedLine>
        </h1>

        <MaskedLine delay={0.9} className="mt-6 max-w-xl">
          <span className="text-sm leading-relaxed text-amber-100/85 sm:text-base">
            Warm, spotless air-conditioned rooms opposite Janata College — the pilgrim's
            pause between Maravante's sunset shores and the sacred hills of Kollur
            Mookambika.
          </span>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={PHONE_TEL}
            data-testid="call-now-button"
            className="group inline-flex items-center gap-3 rounded-full bg-gold px-7 py-4 text-sm font-bold uppercase tracking-widest text-forest-dark transition-all duration-300 hover:scale-[1.04] hover:bg-gold-light"
          >
            <Phone className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
            Call {PHONE_DISPLAY}
          </a>
          <a
            href={WA_DEFAULT}
            target="_blank"
            rel="noreferrer"
            data-testid="whatsapp-booking-button"
            className="group inline-flex items-center gap-3 rounded-full border border-gold/60 px-7 py-4 text-sm font-bold uppercase tracking-widest text-gold-light transition-all duration-300 hover:bg-gold/15"
          >
            <MessageCircle className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
            Book on WhatsApp
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-gold/20 pt-6 text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-100/70"
        >
          <span className="inline-flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 text-gold" /> Opp. Janata College, Hemmadi
          </span>
          <span>24×7 Front Desk</span>
          <span>Free Parking</span>
          <span>AC Deluxe Rooms</span>
        </motion.div>
      </motion.div>

      <motion.a
        href="#rooms"
        aria-label="Scroll to rooms"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 right-6 z-10 hidden text-gold-light/80 sm:block"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="block"
        >
          <ChevronDown className="h-7 w-7" />
        </motion.span>
      </motion.a>
    </section>
  );
}
